import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import EditDialog from './EditDialog';
import AdminManagement from '../components/AdminManagement';
import { useNavigate } from 'react-router-dom';

export default function AdminDashboard() {
  const { adminUsername, isSuperAdmin, logout, isAuthenticated, isLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editId, setEditId] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Show success message and auto-hide after 3 seconds
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Fetch all data with proper error handling
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tutorRes, studentRes, matchesRes] = await Promise.all([
        fetch('/api/admin/tutors', {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }),
        fetch('/api/students', {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }),
        fetch('/api/matches', {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })
      ]);

      if (!tutorRes.ok || !studentRes.ok || !matchesRes.ok) {
        throw new Error('Failed to fetch data from server');
      }

      const tutorsData = await tutorRes.json();
      const studentsData = await studentRes.json();
      const matchesData = await matchesRes.json();

      // Map studentId to all their matches
      const studentMatchesMap = {};
      matchesData.forEach(match => {
        const studentKey = match.studentFirstName + ' ' + match.studentLastName;
        if (!studentMatchesMap[studentKey]) {
          studentMatchesMap[studentKey] = [];
        }
        studentMatchesMap[studentKey].push({
          tutorId: match.tutorId,
          tutor: match.tutorFirstName + ' ' + match.tutorLastName,
          date: match.matchedAt,
          matchId: match.id
        });
      });

      // Attach all matches to each student
      const studentsWithMatches = studentsData.map(s => {
        const studentKey = s.firstName + ' ' + s.lastName;
        const matches = studentMatchesMap[studentKey] || [];
        return {
          ...s,
          matches: matches
        };
      });

      setTutors(tutorsData);
      setStudents(studentsWithMatches);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Delete tutor with error handling
  async function deleteTutor(id) {
    if (!window.confirm('Are you sure you want to delete this tutor?')) return;
    
    setOperationLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/tutors/${id}`, { 
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete tutor');
      }

      setTutors(tutors.filter(t => t.id !== id));
      showSuccess('Tutor deleted successfully');
    } catch (err) {
      console.error('Error deleting tutor:', err);
      alert('Error deleting tutor: ' + err.message);
    } finally {
      setOperationLoading(false);
    }
  }

  // Open edit dialog
  function openEdit(tutor) {
    setEditData(tutor);
    setEditId(tutor.id);
    setEditOpen(true);
  }

  // Save tutor with error handling and data refresh
  async function handleSave(form) {
    setOperationLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/tutors/${editId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update tutor');
      }

      // Refresh data from server to ensure consistency
      await fetchData();
      setEditOpen(false);
      showSuccess('Tutor updated successfully');
    } catch (err) {
      console.error('Error saving tutor:', err);
      alert('Error saving tutor: ' + err.message);
    } finally {
      setOperationLoading(false);
    }
  }

  // Update tutor (for checkbox changes) with error handling
  async function handleTutorUpdate(id, updatedTutor) {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/tutors/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedTutor)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update tutor');
      }

      // Refresh data from server
      await fetchData();
      showSuccess('Tutor updated successfully');
    } catch (err) {
      console.error('Error updating tutor:', err);
      alert('Error updating tutor: ' + err.message);
      // Revert optimistic update by refreshing
      await fetchData();
    }
  }

  // Delete student with error handling
  async function deleteStudent(id) {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    
    setOperationLoading(true);
    try {
      const response = await fetch(`/api/students/${id}`, {  
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete student');
      }

      setStudents(students.filter(s => s.id !== id));
      showSuccess('Student deleted successfully');
    } catch (err) {
      console.error('Error deleting student:', err);
      alert('Error deleting student: ' + err.message);
    } finally {
      setOperationLoading(false);
    }
  }

  // Delete match with error handling
  async function deleteMatch(matchId) {
    if (!window.confirm('Are you sure you want to remove this match?')) return;
    
    setOperationLoading(true);
    try {
      const response = await fetch(`/api/matches/${matchId}`, {  
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete match');
      }

      // Refresh the data to show updated match history
      await fetchData();
      showSuccess('Match removed successfully');
    } catch (err) {
      console.error('Error deleting match:', err);
      alert('Error deleting match: ' + err.message);
    } finally {
      setOperationLoading(false);
    }
  }

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Logout handler
  async function handleLogout() {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
      navigate('/admin/login');
    }
  }

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        fontSize: '1.1rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Redirect if not authenticated (handled by useEffect, but show loading here)
  if (!isAuthenticated) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        fontSize: '1.1rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Error state
  if (error && tutors.length === 0 && students.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        color: '#d32f2f',
        backgroundColor: '#ffebee',
        borderRadius: '8px',
        margin: '2rem'
      }}>
        <h3>Error</h3>
        <p>{error}</p>
        <button
          onClick={fetchData}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{
      padding: 'clamp(1rem, 3vw, 2rem)',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Success Message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#4caf50',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 10000,
          fontSize: '14px',
          fontWeight: '500'
        }}>
          ✅ {successMessage}
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h2 style={{ margin: 0, fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>Admin Dashboard</h2>
          {adminUsername && (
            <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
              Logged in as: <strong>{adminUsername}</strong>
              {isSuperAdmin && <span style={{ color: '#1976d2', marginLeft: '0.5rem' }}>(Super Admin)</span>}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            onClick={fetchData}
            disabled={loading || operationLoading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: loading || operationLoading ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading || operationLoading ? 'not-allowed' : 'pointer',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              fontWeight: '500',
              minHeight: '44px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => {
              if (!loading && !operationLoading) {
                e.target.style.backgroundColor = '#1565c0';
              }
            }}
            onMouseLeave={e => {
              if (!loading && !operationLoading) {
                e.target.style.backgroundColor = '#1976d2';
              }
            }}
          >
            {loading ? '🔄 Refreshing...' : '🔄 Refresh Data'}
          </button>
          <button
            onClick={handleLogout}
            disabled={operationLoading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: operationLoading ? '#ccc' : '#d32f2f',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: operationLoading ? 'not-allowed' : 'pointer',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              fontWeight: '500',
              minHeight: '44px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => {
              if (!operationLoading) {
                e.target.style.backgroundColor = '#b71c1c';
              }
            }}
            onMouseLeave={e => {
              if (!operationLoading) {
                e.target.style.backgroundColor = '#d32f2f';
              }
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Error Banner (if error but data exists) */}
      {error && (tutors.length > 0 || students.length > 0) && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: '6px',
          fontSize: '14px',
          border: '1px solid #ffcdd2'
        }}>
          ⚠️ {error}
        </div>
      )}
      
      {/* Last Refresh Time */}
      {lastRefresh && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          backgroundColor: '#e8f5e8',
          borderRadius: '6px',
          fontSize: '14px',
          color: '#2e7d32'
        }}>
          📅 Last updated: {lastRefresh.toLocaleString('en-US', {
            timeZone: 'America/Los_Angeles',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })} PST
        </div>
      )}
      
      {/* Tutors Table */}
      <h3 style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', marginBottom: '1rem' }}>Tutors</h3>
      <div style={{ overflowX: 'auto', marginBottom: '2rem', border: '1px solid #ddd', borderRadius: '8px' }}>
        <table style={{
          width: '100%',
          minWidth: '1200px',
          borderCollapse: 'collapse',
          backgroundColor: 'white'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>ID</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Email</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>School</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>School Email</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Subject</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Requested Subjects</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Time</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>CSF</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Grad Year</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Booking Link</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Valid*</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tutors.length === 0 ? (
              <tr>
                <td colSpan="12" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  No tutors found
                </td>
              </tr>
            ) : (
              tutors.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{t.id}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px', fontWeight: '500' }}>
                    {t.firstName} {t.lastName}
                  </td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{t.email || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{t.school || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{t.schoolEmail || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>
                    {Array.isArray(t.subjects) ? t.subjects.join(', ') : t.subjects || ''}
                  </td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{t.requestedSubjects || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{t.time || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{t.csf || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{t.gradYear || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>
                    {t.bookingLink ? (
                      <a 
                        href={t.bookingLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                      >
                        {t.bookingLink.length > 30 ? t.bookingLink.substring(0, 30) + '...' : t.bookingLink}
                      </a>
                    ) : (
                      '\u2014'
                    )}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: '13px', fontWeight: '500' }}>
                    {t.valid === 1 ? 'Yes' : 'No'}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <button 
                      onClick={() => openEdit(t)}
                      disabled={operationLoading}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: operationLoading ? '#ccc' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: operationLoading ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        minHeight: '32px'
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Students Table */}
      <h3 style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', marginBottom: '1rem' }}>Students</h3>
      <div style={{ overflowX: 'auto', marginBottom: '2rem', border: '1px solid #ddd', borderRadius: '8px' }}>
        <table style={{
          width: '100%',
          minWidth: '900px',
          borderCollapse: 'collapse',
          backgroundColor: 'white'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>ID</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Personal Email</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>School Email</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>School</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Grad Year</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Match History</th>
              <th style={{ padding: '12px 8px', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  No students found
                </td>
              </tr>
            ) : (
              students.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{s.id}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px', fontWeight: '500' }}>
                    {s.firstName} {s.lastName}
                  </td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{s.personalEmail || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{s.schoolEmail || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{s.school || '\u2014'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{s.gradYear}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>
                    {s.matches && s.matches.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {s.matches.map((match) => (
                          <div key={match.matchId} style={{ 
                            padding: '0.5rem', 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: '4px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div style={{ fontWeight: '500', color: '#495057' }}>
                              Tutor ID: {match.tutorId || 'N/A'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d' }}>
                              {new Date(match.date).toLocaleString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            <button 
                              onClick={() => deleteMatch(match.matchId)}
                              disabled={operationLoading}
                              style={{
                                padding: '0.2rem 0.4rem',
                                backgroundColor: operationLoading ? '#ccc' : '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: operationLoading ? 'not-allowed' : 'pointer',
                                fontSize: '10px',
                                marginTop: '0.25rem'
                              }}
                            >
                              Remove Match
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      '\u2014'
                    )}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <button 
                      onClick={() => deleteStudent(s.id)}
                      disabled={operationLoading}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: operationLoading ? '#ccc' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: operationLoading ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        minHeight: '32px'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Admin Management (Super Admin Only) */}
      {isSuperAdmin && <AdminManagement />}

      {/* Edit Dialog */}
      <EditDialog
        open={editOpen}
        type="tutor"
        data={editData}
        onSave={handleSave}
        onClose={() => setEditOpen(false)}
      />

      {/* Operation Loading Overlay */}
      {operationLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{ margin: 0, color: '#666' }}>Processing...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
}
